import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AuthenticationService, AccountService, setAuthToken, ApiError } from '@/api';
import { OrgMemberService } from '@/api/generated/services/OrgMemberService';
import type { LoginRequest, AccountResponse, OrgMemberResponse } from '@/api/types';
import { getErrorMessage } from '@/types/api';
import { storageService } from '@/services/storageService';

// Storage keys
const TOKEN_KEY = 'dot_access_token';
const REFRESH_TOKEN_KEY = 'dot_refresh_token';
const REMEMBER_EMAIL_KEY = 'dot_remember_email';
const CURRENT_ACCOUNT_KEY = 'dot_current_account';
const CURRENT_ORG_MEMBER_KEY = 'dot_current_org_member';
const SELECTED_WORKSPACE_KEY = 'dot_selected_workspace';

/**
 * Token management utilities using storage service
 */
export const saveTokens = (accessToken: string, refreshToken: string) => {
  storageService.setItem(TOKEN_KEY, accessToken);
  storageService.setItem(REFRESH_TOKEN_KEY, refreshToken);
  setAuthToken(accessToken);
};

export const getStoredTokens = () => {
  const accessToken = storageService.getItem(TOKEN_KEY);
  const refreshToken = storageService.getItem(REFRESH_TOKEN_KEY);
  return { accessToken, refreshToken };
};

export const clearTokens = () => {
  storageService.removeItem(TOKEN_KEY);
  storageService.removeItem(REFRESH_TOKEN_KEY);
  storageService.removeItem(SELECTED_WORKSPACE_KEY); // Clear selected workspace on logout
  clearCurrentAccount(); // Clear current account on logout
  clearCurrentOrgMember(); // Clear current org member on logout
  setAuthToken(null);
};

/**
 * Remember email utilities using storage service
 */
export const saveRememberedEmail = (email: string) => {
  storageService.setItem(REMEMBER_EMAIL_KEY, email);
};

export const getRememberedEmail = () => {
  return storageService.getItem(REMEMBER_EMAIL_KEY) || '';
};

export const clearRememberedEmail = () => {
  storageService.removeItem(REMEMBER_EMAIL_KEY);
};

/**
 * Account management utilities using storage service
 */
export const saveCurrentAccount = (account: AccountResponse) => {
  storageService.setObject(CURRENT_ACCOUNT_KEY, account);
};

export const getCurrentAccount = (): AccountResponse | null => {
  return storageService.getObject<AccountResponse>(CURRENT_ACCOUNT_KEY);
};

export const clearCurrentAccount = () => {
  storageService.removeItem(CURRENT_ACCOUNT_KEY);
};

/**
 * Org member management utilities using storage service
 */
export const saveCurrentOrgMember = (orgMember: OrgMemberResponse) => {
  storageService.setObject(CURRENT_ORG_MEMBER_KEY, orgMember);
};

export const getCurrentOrgMember = (): OrgMemberResponse | null => {
  return storageService.getObject<OrgMemberResponse>(CURRENT_ORG_MEMBER_KEY);
};

export const clearCurrentOrgMember = () => {
  storageService.removeItem(CURRENT_ORG_MEMBER_KEY);
};

// Auth state interface
interface AuthState {
  user: AccountResponse | null;
  orgMember: OrgMemberResponse | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
  rememberEmail: boolean;
  autoLogin: boolean;
}

// Initial state
const initialState: AuthState = {
  user: getCurrentAccount(), // Load saved account from localStorage
  orgMember: getCurrentOrgMember(), // Load saved org member from localStorage
  isAuthenticated: false,
  isInitialized: false,
  isLoading: false,
  isRefreshing: false,
  error: null,
  rememberEmail: false,
  autoLogin: false,
};

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async (
    credentials: LoginRequest & { rememberEmail?: boolean; autoLogin?: boolean },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const { rememberEmail, autoLogin, ...loginData } = credentials;

      const response = await AuthenticationService.appsAuthenticationViewsLogin({
        requestBody: loginData
      });

      // Save tokens
      saveTokens(response.access_token, response.refresh_token);

      // Handle remember email
      if (rememberEmail) {
        saveRememberedEmail(loginData.email);
      } else {
        clearRememberedEmail();
      }

      // Get user account info
      const userResponse = await AccountService.appsAccountViewsGetCurrentAccount();

      // Save current account to localStorage
      saveCurrentAccount(userResponse);

      // Initialize workspaces after successful login
      try {
        const { fetchWorkspaces } = await import('./workspaceSlice');
        await dispatch(fetchWorkspaces({}));
        console.log('✅ Workspaces fetched successfully during login');
      } catch (workspaceError) {
        console.warn('Failed to fetch workspaces during login:', workspaceError);
        // Don't fail the login if workspace fetching fails
      }

      return {
        tokens: response,
        user: userResponse,
        rememberEmail: rememberEmail || false,
        autoLogin: autoLogin || false,
      };
    } catch (error: any) {
      // Check if it's an ApiError with 400 status
      if (error instanceof ApiError && error.status === 400) {
        return rejectWithValue('비밀번호가 틀렸거나 유효하지 않은 계정입니다.');
      }

      const errorMessage = getErrorMessage(error);
      return rejectWithValue(errorMessage || '로그인에 실패했습니다.');
    }
  }
);

export const logout = createAsyncThunk('auth/logout', async (_, { dispatch }) => {
  try {
    await AuthenticationService.appsAuthenticationViewsLogout({});
    clearTokens();
    // Clear workspace state as well
    const { clearWorkspace } = await import('./workspaceSlice');
    dispatch(clearWorkspace());
    return;
  } catch (error: any) {
    // Clear tokens even if API call fails
    clearTokens();
    // Clear workspace state as well
    const { clearWorkspace } = await import('./workspaceSlice');
    dispatch(clearWorkspace());
    throw error;
  }
});

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      const { refreshToken: storedRefreshToken } = getStoredTokens();
      
      if (!storedRefreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await AuthenticationService.appsAuthenticationViewsRefreshTokenView({
        requestBody: { refresh_token: storedRefreshToken }
      });

      saveTokens(response.access_token, storedRefreshToken);

      return response;
    } catch (error: any) {
      clearTokens();
      return rejectWithValue(error.message || '토큰 갱신에 실패했습니다.');
    }
  }
);

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await AccountService.appsAccountViewsGetCurrentAccount();

      // Save current account to localStorage
      saveCurrentAccount(response);

      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || '사용자 정보 조회에 실패했습니다.');
    }
  }
);

export const getCurrentOrgMemberInfo = createAsyncThunk(
  'auth/getCurrentOrgMemberInfo',
  async (orgId: string, { rejectWithValue }) => {
    try {
      const response = await OrgMemberService.appsOrganizationViewsGetCurrentOrgMember({
        orgId
      });

      // Save current org member to localStorage
      saveCurrentOrgMember(response);

      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || '기관 멤버 정보 조회에 실패했습니다.');
    }
  }
);

// Initialize auth and workspaces
export const initializeAuth = createAsyncThunk(
  'auth/initializeAuth',
  async (_, { dispatch }) => {
    const { accessToken } = getStoredTokens();
    const savedAccount = getCurrentAccount();
    const savedOrgMember = getCurrentOrgMember();

    if (accessToken) {
      setAuthToken(accessToken);

      // If user is authenticated, fetch workspaces
      try {
        const { fetchWorkspaces } = await import('./workspaceSlice');
        await dispatch(fetchWorkspaces({}));
      } catch (workspaceError) {
        console.warn('Failed to fetch workspaces during initialization:', workspaceError);
        // Don't fail the initialization if workspace fetching fails
      }

      return {
        user: savedAccount,
        orgMember: savedOrgMember,
        isAuthenticated: true
      };
    }

    return {
      user: null,
      orgMember: null,
      isAuthenticated: false
    };
  }
);

// Refresh user data after account update
export const refreshUserData = createAsyncThunk(
  'auth/refreshUserData',
  async (orgId: string | undefined, { rejectWithValue }) => {
    try {
      // 1. 현재 계정 정보 새로고침
      const accountResponse = await AccountService.appsAccountViewsGetCurrentAccount();

      // 2. localStorage와 Redux state 업데이트
      saveCurrentAccount(accountResponse);

      let orgMemberResponse = null;

      // 3. orgId가 제공된 경우 조직 멤버 정보도 새로고침
      if (orgId) {
        try {
          orgMemberResponse = await OrgMemberService.appsOrganizationViewsGetCurrentOrgMember({
            orgId
          });

          // localStorage와 Redux state 업데이트
          saveCurrentOrgMember(orgMemberResponse);
        } catch (orgError) {
          console.warn('Failed to refresh org member info:', orgError);
          // 조직 멤버 정보 실패는 전체 실패로 처리하지 않음
        }
      }

      return {
        account: accountResponse,
        orgMember: orgMemberResponse
      };
    } catch (error: any) {
      console.error('Failed to refresh user data:', error);
      return rejectWithValue(error.message || '사용자 정보 새로고침에 실패했습니다.');
    }
  }
);

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setRememberEmail: (state, action: PayloadAction<boolean>) => {
      state.rememberEmail = action.payload;
    },
    setAutoLogin: (state, action: PayloadAction<boolean>) => {
      state.autoLogin = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.rememberEmail = action.payload.rememberEmail;
        state.autoLogin = action.payload.autoLogin;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.error = action.payload as string;
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.orgMember = null;
        state.error = null;
        state.rememberEmail = false;
        state.autoLogin = false;
      })
      .addCase(logout.rejected, (state) => {
        // Clear state even if logout API fails (tokens are already cleared)
        state.isAuthenticated = false;
        state.user = null;
        state.orgMember = null;
        state.error = null;
        state.rememberEmail = false;
        state.autoLogin = false;
      })
      // Refresh token
      .addCase(refreshToken.pending, (state) => {
        state.isRefreshing = true;
        state.error = null;
      })
      .addCase(refreshToken.fulfilled, (state) => {
        state.isAuthenticated = true;
        state.isRefreshing = false;
        state.error = null;
      })
      .addCase(refreshToken.rejected, (state) => {
        state.isAuthenticated = false;
        state.isRefreshing = false;
        state.user = null;
        state.error = null;
      })
      // Get current user
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(getCurrentUser.rejected, (state) => {
        state.isAuthenticated = false;
        state.user = null;
      })
      // Get current org member
      .addCase(getCurrentOrgMemberInfo.fulfilled, (state, action) => {
        state.orgMember = action.payload;
      })
      .addCase(getCurrentOrgMemberInfo.rejected, (state) => {
        state.orgMember = null;
      })
      // Initialize auth
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.orgMember = action.payload.orgMember;
        state.isAuthenticated = action.payload.isAuthenticated;
        state.isInitialized = true;
      })
      .addCase(initializeAuth.rejected, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.orgMember = null;
        state.isInitialized = true;
      })
      // Refresh user data
      .addCase(refreshUserData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(refreshUserData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.account;
        if (action.payload.orgMember) {
          state.orgMember = action.payload.orgMember;
        }
        state.error = null;
      })
      .addCase(refreshUserData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setRememberEmail, setAutoLogin } = authSlice.actions;
export default authSlice.reducer;